import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Loader2, Building2, FolderTree, Search, ListOrdered } from "lucide-react";
import { tarifasApi, errMsg } from "./api";
import { AliasSuggestions } from "./AliasSuggestions";

const EMPTY = { name: "", aliases: "", country: "", destination: "", catalog_url: "", price_basis: "auto", agency_discount_pct: "", taxes_included_default: false, tax_pct: "", supplements_separate: false, notes: "" };

const toForm = (p) => ({
  ...EMPTY, ...p, aliases: (p.aliases || []).join(", "), price_basis: p.price_basis || "auto",
  agency_discount_pct: p.agency_discount_pct ?? "", tax_pct: p.tax_pct ?? "",
});

const toBody = (f) => ({
  name: f.name.trim(),
  aliases: f.aliases.split(",").map((a) => a.trim()).filter(Boolean),
  country: f.country, destination: f.destination, catalog_url: f.catalog_url,
  price_basis: f.price_basis === "auto" ? null : f.price_basis,
  agency_discount_pct: f.agency_discount_pct === "" ? null : Number(f.agency_discount_pct),
  taxes_included_default: !!f.taxes_included_default, tax_pct: f.tax_pct === "" ? null : Number(f.tax_pct), notes: f.notes,
  supplements_separate: !!f.supplements_separate,
});

export const ProvidersTable = ({ providers, onChange, onOpenTarifas }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("all");
  const [zone, setZone] = useState("all");

  const norm = (s) => (s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const countries = [...new Set(providers.map((p) => p.country).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es"));
  const zones = [...new Set(providers.filter((p) => country === "all" || p.country === country).map((p) => p.destination).filter(Boolean))].sort((a, b) => a.localeCompare(b, "es"));
  const visible = providers.filter((p) =>
    (country === "all" || p.country === country) && (zone === "all" || p.destination === zone)
    && (!q || norm(p.name).includes(norm(q)) || (p.aliases || []).some((a) => norm(a).includes(norm(q)))));

  const fromDrive = async () => {
    if (!window.confirm("Se creará un proveedor por cada carpeta de Drive (País / Destino / Proveedor) que aún no tenga uno. Después podrás editar nombres y alias. ¿Continuar?")) return;
    setCreating(true);
    try {
      const r = await tarifasApi.providersFromDrive();
      toast.success(r.count ? `${r.count} proveedores creados desde las carpetas de Drive` : "Todas las carpetas de Drive ya tienen proveedor");
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e, "No se pudieron crear los proveedores"));
    } finally {
      setCreating(false);
    }
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      if (form.id) await tarifasApi.updateProvider(form.id, toBody(form));
      else await tarifasApi.createProvider(toBody(form));
      toast.success("Proveedor guardado");
      setForm(null);
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo guardar el proveedor"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar el proveedor "${p.name}"? Se mantendrán las correspondencias e historial.`)) return;
    try {
      await tarifasApi.deleteProvider(p.id);
      toast.success("Proveedor eliminado");
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e, "No se pudo eliminar"));
    }
  };

  return (
    <div>
    <AliasSuggestions providers={providers} onChanged={onChange} onCreateProvider={(prefill) => setForm({ ...EMPTY, ...prefill })} />
    <Card className="rounded-md" data-testid="providers-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div>
          <h3 className="font-heading text-base font-bold flex items-center gap-2"><Building2 className="h-4 w-4" /> Proveedores y diminutivos</h3>
          <p className="text-xs text-muted-foreground">Los alias se buscan en las notas de precio y en el operador de cada actividad de KimKim (sin distinguir mayúsculas, espacios ni separadores).</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="providers-from-drive-button" size="sm" variant="outline" className="rounded-md" onClick={fromDrive} disabled={creating}>
            {creating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <FolderTree className="h-3 w-3 mr-1" />} Crear desde carpetas de Drive
          </Button>
          <Button data-testid="provider-add-button" size="sm" className="rounded-md" onClick={() => setForm({ ...EMPTY })}><Plus className="h-3 w-3 mr-1" /> Nuevo proveedor</Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-wrap items-center gap-2 pb-2" data-testid="providers-filters">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-muted-foreground" />
            <Input data-testid="providers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o alias…" className="h-8 w-64 pl-7 rounded-md text-xs" />
          </div>
          <Select value={country} onValueChange={(v) => { setCountry(v); setZone("all"); }}>
            <SelectTrigger data-testid="providers-country-filter" className="h-8 w-44 rounded-md text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los países</SelectItem>
              {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={zone} onValueChange={setZone}>
            <SelectTrigger data-testid="providers-zone-filter" className="h-8 w-48 rounded-md text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las zonas</SelectItem>
              {zones.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground ml-auto" data-testid="providers-count">{visible.length} de {providers.length} proveedores</span>
        </div>
        <div className="divide-y">
        {providers.length === 0 && <p className="text-sm text-muted-foreground py-4">Aún no hay proveedores.</p>}
        {providers.length > 0 && visible.length === 0 && <p className="text-sm text-muted-foreground py-4" data-testid="providers-no-results">Ningún proveedor coincide con el filtro.</p>}
        {visible.map((p) => (
          <div key={p.id} className="py-3 flex flex-wrap items-start justify-between gap-3" data-testid={`provider-row-${p.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-sm">{p.name}</span>
                {(p.aliases || []).map((a) => <Badge key={a} className="rounded-md bg-blue-50 text-blue-800 hover:bg-blue-50 font-mono">{a}</Badge>)}
                {p.processed_files_count > 0 && <Badge className="rounded-md bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`provider-has-tarifas-${p.id}`}>tarifas cargadas</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {[p.country, p.destination].filter(Boolean).join(" · ") || "Sin destino"} · precios: {p.price_basis === "net" ? "netos" : p.price_basis === "public" ? `públicos${p.agency_discount_pct != null ? ` (−${p.agency_discount_pct}% agencia)` : ""}` : "según documento"}
                {p.taxes_included_default ? " · impuestos incluidos por defecto" : p.tax_pct != null ? ` · +${p.tax_pct}% IVA si no incluido` : ""}
              </p>
              <p className="text-xs text-muted-foreground">{p.kimkim_count} actividades en KimKim · {p.confirmed_count} correspondencias confirmadas · {p.files_count} archivos ({p.processed_files_count} procesados)</p>
              {p.notes && <p className="text-xs italic text-muted-foreground mt-1">{p.notes}</p>}
            </div>
            <div className="flex gap-1">
              <Button data-testid={`provider-tarifas-${p.id}`} size="sm" variant={p.processed_files_count > 0 ? "default" : "outline"} className="rounded-md" onClick={() => onOpenTarifas?.(p)}><ListOrdered className="h-3 w-3 mr-1" /> Ver tarifas</Button>
              <Button data-testid={`provider-edit-${p.id}`} size="sm" variant="outline" className="rounded-md" onClick={() => setForm(toForm(p))}><Pencil className="h-3 w-3" /></Button>
              <Button data-testid={`provider-delete-${p.id}`} size="sm" variant="outline" className="rounded-md text-red-600" onClick={() => remove(p)}><Trash2 className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
        </div>
      </CardContent>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="rounded-md max-w-lg" data-testid="provider-dialog">
          <DialogHeader><DialogTitle>{form?.id ? "Editar proveedor" : "Nuevo proveedor"}</DialogTitle></DialogHeader>
          {form && (
            <div className="grid gap-3 text-sm">
              <label>Nombre del proveedor<Input data-testid="provider-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-md mt-1" placeholder="Catalan Trails" /></label>
              <label>Diminutivos / alias en KimKim (separados por comas; el primero se usa en la marca "2027 // alias")
                <Input data-testid="provider-aliases-input" value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} className="rounded-md mt-1" placeholder="C Trails, CTrails, CT" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label>País<Input data-testid="provider-country-input" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="rounded-md mt-1" /></label>
                <label>Destino<Input data-testid="provider-destination-input" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} className="rounded-md mt-1" /></label>
              </div>
              <label>URL del catálogo KimKim de este destino (opcional, si no se usa la general)
                <Input data-testid="provider-catalog-input" value={form.catalog_url} onChange={(e) => setForm({ ...form, catalog_url: e.target.value })} className="rounded-md mt-1 text-xs" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label>Los precios del proveedor son…
                  <Select value={form.price_basis} onValueChange={(v) => setForm({ ...form, price_basis: v })}>
                    <SelectTrigger data-testid="provider-basis-select" className="rounded-md mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Según indique cada documento</SelectItem>
                      <SelectItem value="net">Siempre netos</SelectItem>
                      <SelectItem value="public">Públicos (aplicar descuento agencia)</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <label>Descuento de agencia % (si públicos)
                  <Input data-testid="provider-discount-input" type="number" value={form.agency_discount_pct} onChange={(e) => setForm({ ...form, agency_discount_pct: e.target.value })} className="rounded-md mt-1" placeholder="10" />
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="provider-taxes-checkbox" type="checkbox" checked={!!form.taxes_included_default} onChange={(e) => setForm({ ...form, taxes_included_default: e.target.checked })} />
                Sus tarifas incluyen impuestos aunque el documento no lo diga
              </label>
              <label>IVA a sumar al neto (%) cuando el tarifario diga que los impuestos NO van incluidos (vacío = preguntar)
                <Input data-testid="provider-tax-input" type="number" value={form.tax_pct} onChange={(e) => setForm({ ...form, tax_pct: e.target.value })} className="rounded-md mt-1" placeholder="21" />
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input data-testid="provider-supplements-checkbox" type="checkbox" checked={!!form.supplements_separate} onChange={(e) => setForm({ ...form, supplements_separate: e.target.checked })} />
                Sus suplementos obligatorios se cobran aparte: no se suman al precio (quedan como aviso)
              </label>
              <label>Condiciones comerciales / notas<Input data-testid="provider-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-md mt-1" /></label>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="rounded-md" onClick={() => setForm(null)}>Cancelar</Button>
            <Button data-testid="provider-save-button" className="rounded-md" onClick={save} disabled={saving}>{saving && <Loader2 className="h-3 w-3 animate-spin mr-1" />} Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
    </div>
  );
};
